/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { vehicles as demoVehicles } from '@/mocks/vehicles';

export interface VehicleSpec {
  label: string;
  value: string;
}

export type VehicleLocation = 'Le Tampon' | 'Saint-Pierre';

export interface Vehicle {
  id: string;
  brand: string;
  model: string;
  motorisation: string;
  price: number;
  promoPrice?: number | null;
  year: number;
  mileage: number;
  fuel: string;
  gearbox: string;
  location: VehicleLocation;
  photos: string[];
  specs: VehicleSpec[];
  description: string;
  equipment: string[];
}

export interface OpeningHourRow {
  day: string;
  time: string;
}

interface SiteSettings {
  heroImage?: string | null;
}

interface SiteDataContextValue {
  vehicles: Vehicle[];
  hours: Record<VehicleLocation, OpeningHourRow[]>;
  settings: SiteSettings;
  loading: boolean;
  error: boolean;
}

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const defaultHours: OpeningHourRow[] = [
  { day: 'Lundi', time: '08:00 – 12:00 / 14:00 – 17:30' },
  { day: 'Mardi', time: '08:00 – 12:00 / 14:00 – 17:30' },
  { day: 'Mercredi', time: '08:00 – 12:00 / 14:00 – 17:30' },
  { day: 'Jeudi', time: '08:00 – 12:00 / 14:00 – 17:30' },
  { day: 'Vendredi', time: '08:00 – 12:00 / 14:00 – 17:30' },
  { day: 'Samedi', time: '09:00 – 12:00' },
  { day: 'Dimanche', time: 'Fermé' },
];

const initialValue: SiteDataContextValue = {
  vehicles: [],
  hours: {
    'Le Tampon': defaultHours,
    'Saint-Pierre': defaultHours,
  },
  settings: {},
  loading: true,
  error: false,
};

const SiteDataContext = createContext<SiteDataContextValue>(initialValue);

function formatHours(raw: any): OpeningHourRow[] {
  return DAY_NAMES.map((day, index) => {
    const value = raw?.[String(index + 1)] ?? raw?.[index + 1];
    if (!Array.isArray(value)) {
      return defaultHours[index];
    }

    const [morningOpen, morningClose, afternoonOpen, afternoonClose, closed] = value;
    if (Number(closed) === 1) {
      return { day, time: 'Fermé' };
    }

    const periods: string[] = [];
    if (morningOpen && morningClose) periods.push(`${morningOpen} – ${morningClose}`);
    if (afternoonOpen && afternoonClose) periods.push(`${afternoonOpen} – ${afternoonClose}`);
    return { day, time: periods.length ? periods.join(' / ') : 'Fermé' };
  });
}

function devFallbackVehicles(): Vehicle[] {
  return demoVehicles.map((vehicle) => ({
    id: vehicle.id,
    brand: vehicle.brand,
    model: vehicle.model,
    motorisation: vehicle.motorisation,
    price: vehicle.price,
    promoPrice: vehicle.promoPrice ?? null,
    year: vehicle.year,
    mileage: vehicle.mileage,
    fuel: vehicle.fuel,
    gearbox: vehicle.gearbox,
    location: vehicle.location,
    photos: vehicle.photos,
    specs: [
      { label: 'Puissance fiscale', value: vehicle.puissanceFiscale },
      ...vehicle.specs,
    ],
    description: vehicle.description,
    equipment: vehicle.equipment,
  }));
}

export function SiteDataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SiteDataContextValue>(initialValue);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetch('/api/public.php', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('API unavailable');
        const payload = await response.json();
        if (!payload?.ok) throw new Error('Invalid API response');

        if (!cancelled) {
          setState({
            vehicles: Array.isArray(payload.vehicles) ? payload.vehicles : [],
            hours: {
              'Le Tampon': formatHours(payload.hours?.['Le Tampon']),
              'Saint-Pierre': formatHours(payload.hours?.['Saint-Pierre']),
            },
            settings: payload.settings ?? {},
            loading: false,
            error: false,
          });
        }
      } catch {
        if (!cancelled) {
          setState({
            vehicles: import.meta.env.DEV ? devFallbackVehicles() : [],
            hours: initialValue.hours,
            settings: {},
            loading: false,
            error: true,
          });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>;
}

export function useSiteData() {
  return useContext(SiteDataContext);
}
